import { useCallback } from "react";
import { MONTH_END_CLOSE_VIEW_ID } from "@components/tasks/views/OrganizationTaskViews";

const CLOSE_ID_URL_PARAM = "closeId";

/**
 * Extracts the UUID portion from a prefixed close ID (e.g. "close:uuid" → "uuid").
 * Returns undefined when the input has no ":" separator or the portion after it is empty.
 */
export const parseCloseId = (closeId: string): string | undefined =>
    closeId.split(":")[1] || undefined;

const getPendingCloseIdKey = (workspaceId: string) =>
    `pendingCloseId_${workspaceId}`;

/** Whether a pending close id waits for the workspace, without consuming it. */
export const hasPendingCloseId = (workspaceId: string): boolean =>
    Boolean(sessionStorage.getItem(getPendingCloseIdKey(workspaceId)));

/**
 * Consume a pending close id from sessionStorage (legacy same-tab flow).
 * The new-tab flow passes closeId as a URL query param which TasksPage already
 * reads independently via useQueryParams, so this is only needed as a fallback.
 */
export const consumePendingCloseId = (workspaceId: string): string | null => {
    const key = getPendingCloseIdKey(workspaceId);
    const value = sessionStorage.getItem(key);
    if (value) {
        sessionStorage.removeItem(key);
    }
    return value;
};

/** The /open query every work link shares: the workspace to enter and the work to land on. */
const buildOpenWorkSearchParams = ({
    workspaceId,
    closeId,
}: {
    workspaceId: string;
    closeId: string;
}) =>
    new URLSearchParams({
        workspaceId,
        target: "work",
        [CLOSE_ID_URL_PARAM]: closeId,
    });

/**
 * Builds the URL for opening a work (close application) row without navigating.
 * Used by both useOpenWorkRow and WorkActionsCell's Copy link action.
 */
export const buildWorkRowUrl = (
    workspaceId: string,
    closeId: string,
): string => {
    const url = new URL("/open", window.location.origin);
    url.search = buildOpenWorkSearchParams({ workspaceId, closeId }).toString();
    return url.toString();
};

/**
 * The in-app /open path that switches into the work's workspace and lands its
 * Tasks page on Month-end close, filtered to the work.
 */
export const getOpenClientWorkLink = ({
    workspaceId,
    closeId,
}: {
    workspaceId: string;
    closeId: string;
}): string => {
    const searchParams = buildOpenWorkSearchParams({ workspaceId, closeId });
    searchParams.set("view", MONTH_END_CLOSE_VIEW_ID);
    return `/open?${searchParams.toString()}`;
};

/** The /open link for a task on the Tasks page, optionally with its chat open. */
export const buildTaskOpenUrl = ({
    workspaceId,
    taskId,
    panel,
}: {
    workspaceId: string;
    taskId: string;
    panel?: "chat";
}): string => {
    const url = new URL("/open", window.location.origin);
    url.searchParams.set("workspaceId", workspaceId);
    url.searchParams.set("target", "task");
    url.searchParams.set("taskId", taskId);
    if (panel) {
        url.searchParams.set("panel", panel);
    }
    return url.toString();
};

/**
 * Opens a work (close application) row in a new browser tab.
 *
 * Uses the /open route so that:
 * 1. The workspace scope is confirmed active before any navigation occurs
 *    (ProductEntityOpenRoute handles the two-step initializeScope → navigate).
 * 2. The browser URL is clean after landing (no ?workspaceId= residue).
 * 3. TasksPage receives ?closeId= only after workspace is settled, making
 *    the close filter reliable.
 */
export const useOpenWorkRow = () =>
    useCallback((workspaceId: string, closeId: string) => {
        window.open(
            buildWorkRowUrl(workspaceId, closeId),
            "_blank",
            "noopener,noreferrer",
        );
    }, []);
